import { CollectionsListPageStore } from "@/store/pages/CollectionsListPageStore/CollectionsListPageStore";
import { CollectionsViewPageStore } from "@/store/pages/CollectionsViewPageStore/CollectionsViewPageStore";
import { NotesListPageStore } from "@/store/pages/NotesListPageStore";
import { TrashPageStore } from "@/store/pages/TrashPageStore";
import { SearchPageStore } from "@/store/pages/SearchPageStore/SearchPageStore";
import { TemplatesListPageStore } from "@/store/pages/TemplatesListPageStore/TemplatesListPageStore";
import { TemplatesViewPageStore } from "@/store/pages/TemplatesViewPageStore/TemplatesViewPageStore";
import { SessionsListPageStore } from "@/store/pages/SessionsListPageStore/SessionsListPageStore";
import { SessionsViewPageStore } from "@/store/pages/SessionsViewPageStore/SessionsViewPageStore";
import { AudioRecordingsListPageStore } from "@/store/pages/AudioRecordingsListPageStore";
import { AppStore, PublicAppStore } from "@/store";
import { makeAutoObservable } from "mobx";
import { AppSubStoreArgs } from "@/store/types";
import { Tab } from "@/store/navigation/types";
import { To, matchPath } from "react-router";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { appRoutes } from "@/app/router";

// Extended args that include tabId for tab-specific stores
interface TabPageStoreArgs extends AppSubStoreArgs {
  tabId: string;
  publicAppStore: PublicAppStore;
}

export type PageStoreType =
  | "notesListPage"
  | "notesViewPage"
  | "collectionsListPage"
  | "collectionsViewPage"
  | "searchPage"
  | "templatesListPage"
  | "templatesViewPage"
  | "trashPage"
  | "sessionsListPage"
  | "sessionsViewPage"
  | "audioRecordingsListPage";

export class TabPageStoreManager {
  private tabStores = new Map<string, Map<PageStoreType, unknown>>();
  private appStore: AppStore;
  private api: AppSubStoreArgs["api"];
  private pusher: AppSubStoreArgs["pusher"];

  constructor(injectedDeps: AppSubStoreArgs) {
    this.appStore = injectedDeps.store;
    this.api = injectedDeps.api;
    this.pusher = injectedDeps.pusher;
    makeAutoObservable(this);
  }

  getPageStore(activeTab: Tab | null, activeTabPath: To, pageType: PageStoreType) {
    const tabId = activeTab?.id ?? "default";

    if (!this.tabStores.has(tabId)) {
      this.tabStores.set(tabId, new Map());
    }
    const tabStoreMap = this.tabStores.get(tabId)!;

    const injectedDeps: TabPageStoreArgs = {
      store: this.appStore,
      api: this.api,
      pusher: this.pusher,
      publicAppStore: this.appStore.publicAppStore,
      tabId: tabId,
    };

    let pageStore: unknown;

    if (!tabStoreMap.has(pageType)) {
      switch (pageType) {
        case "notesListPage":
          pageStore = new NotesListPageStore(injectedDeps);
          break;
        case "collectionsListPage":
          pageStore = new CollectionsListPageStore(injectedDeps);
          break;
        case "notesViewPage": {
          const noteId = this.extractNoteIdFromPath(activeTabPath);
          if (!noteId) return null;
          pageStore = new NotesViewPageStore({ ...injectedDeps, noteId });
          break;
        }
        case "collectionsViewPage": {
          const collectionId = this.extractCollectionIdFromPath(activeTabPath);
          if (!collectionId) return null;
          pageStore = new CollectionsViewPageStore({ ...injectedDeps, collectionId });
          break;
        }
        case "searchPage": {
          pageStore = new SearchPageStore({ ...injectedDeps, path: activeTabPath, tabId });
          break;
        }
        case "templatesListPage":
          pageStore = new TemplatesListPageStore(injectedDeps);
          break;
        case "templatesViewPage": {
          const templateId = this.extractTemplateIdFromPath(activeTabPath);
          if (!templateId) return null;
          pageStore = new TemplatesViewPageStore({ ...injectedDeps, templateId });
          break;
        }
        case "trashPage":
          pageStore = new TrashPageStore(injectedDeps);
          break;
        case "sessionsListPage":
          pageStore = new SessionsListPageStore(injectedDeps);
          break;
        case "audioRecordingsListPage":
          pageStore = new AudioRecordingsListPageStore(injectedDeps);
          break;
        case "sessionsViewPage": {
          const sessionId = this.extractSessionIdFromPath(activeTabPath);
          if (!sessionId) return null;
          pageStore = new SessionsViewPageStore({ ...injectedDeps, sessionId });
          break;
        }
        default:
          throw new Error(`Unknown page store type: ${pageType}`);
      }

      tabStoreMap.set(pageType, pageStore);

      // Initialize the store if it has an initialize method
      if (
        pageStore &&
        typeof pageStore === "object" &&
        "initialize" in pageStore &&
        typeof pageStore.initialize === "function"
      ) {
        pageStore.initialize();
      }
    } else {
      pageStore = tabStoreMap.get(pageType);
    }

    // Update IDs if they have changed (the stores will react to these changes)
    if (pageType === "notesViewPage") {
      const noteId = this.extractNoteIdFromPath(activeTabPath);
      const notesViewPageStore = pageStore as NotesViewPageStore;
      if (noteId && noteId !== notesViewPageStore.noteId) {
        notesViewPageStore.setNoteId(noteId);
      }
    }

    if (pageType === "collectionsViewPage") {
      const collectionId = this.extractCollectionIdFromPath(activeTabPath);
      const collectionsViewPageStore = pageStore as CollectionsViewPageStore;
      if (collectionId && collectionId !== collectionsViewPageStore.collectionId) {
        collectionsViewPageStore.setCollectionId(collectionId);
      }
    }

    if (pageType === "templatesViewPage") {
      const templateId = this.extractTemplateIdFromPath(activeTabPath);
      const templatesViewPageStore = pageStore as TemplatesViewPageStore;
      if (templateId && templateId !== templatesViewPageStore.templateId) {
        templatesViewPageStore.setTemplateId(templateId);
      }
    }

    if (pageType === "sessionsViewPage") {
      const sessionId = this.extractSessionIdFromPath(activeTabPath);
      const sessionsViewPageStore = pageStore as SessionsViewPageStore;
      if (sessionId && sessionId !== sessionsViewPageStore.sessionId) {
        sessionsViewPageStore.setSessionId(sessionId);
      }
    }

    if (pageType === "searchPage") {
      const searchPageStore = pageStore as SearchPageStore;
      if (activeTabPath !== searchPageStore.filters.tabPath) {
        searchPageStore.filters.setTabPath(activeTabPath);
      }
    }

    if (pageType === "notesListPage") {
      const notesListPageStore = pageStore as NotesListPageStore;
      const importBatchId = this.extractImportBatchIdFromPath(activeTabPath);
      if (importBatchId && importBatchId !== notesListPageStore.importBatchId) {
        notesListPageStore.setImportBatchId(importBatchId);
      }
    }

    if (pageType === "collectionsListPage") {
      const collectionsListPageStore = pageStore as CollectionsListPageStore;
      const importBatchId = this.extractImportBatchIdFromPath(activeTabPath);
      if (importBatchId && importBatchId !== collectionsListPageStore.importBatchId) {
        collectionsListPageStore.setImportBatchId(importBatchId);
      }
    }

    return tabStoreMap.get(pageType);
  }

  removeTabStores(tabId: string) {
    const tabStoreMap = this.tabStores.get(tabId);
    if (tabStoreMap) {
      // Clean up any subscriptions or resources
      tabStoreMap.forEach(store => {
        if (
          store &&
          typeof store === "object" &&
          "dispose" in store &&
          typeof store.dispose === "function"
        ) {
          store.dispose();
        }
      });
      this.tabStores.delete(tabId);
    }
  }

  hasPageStore(tabId: string, pageType: PageStoreType): boolean {
    return this.tabStores.get(tabId)?.has(pageType) ?? false;
  }

  getAllTabIds(): string[] {
    return Array.from(this.tabStores.keys());
  }

  // Helper methods to extract IDs from paths
  private extractNoteIdFromPath(path: To): string | undefined {
    const pathName = typeof path === "string" ? path : path.pathname;

    if (!pathName) return undefined;

    const noteIdMatch = matchPath(
      appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
      pathName
    );

    return noteIdMatch?.params.noteId;
  }

  private extractCollectionIdFromPath(path: To): string | undefined {
    const pathName = typeof path === "string" ? path : path.pathname;

    if (!pathName) return undefined;

    const collectionIdMatch = matchPath(
      appRoutes.collectionsView({ params: { collectionId: ":collectionId" }, starPattern: true }),
      pathName
    );

    return collectionIdMatch?.params.collectionId;
  }

  private extractTemplateIdFromPath(path: To): string | undefined {
    const pathName = typeof path === "string" ? path : path.pathname;

    if (!pathName) return undefined;

    const templateIdMatch = matchPath(
      appRoutes.templatesView({ params: { templateId: ":templateId" }, starPattern: true }),
      pathName
    );

    return templateIdMatch?.params.templateId;
  }

  private extractSessionIdFromPath(path: To): string | undefined {
    const pathName = typeof path === "string" ? path : path.pathname;

    if (!pathName) return undefined;

    const sessionIdMatch = matchPath(
      appRoutes.sessionsView({ params: { sessionId: ":sessionId" }, starPattern: true }),
      pathName
    );

    return sessionIdMatch?.params.sessionId;
  }

  private extractImportBatchIdFromPath(path: To): string | undefined {
    const pathName = typeof path === "string" ? path : path.pathname;

    if (!pathName) return undefined;

    const importBatchIdNotesPagesMatch = matchPath(
      appRoutes.importBatchNotes({
        params: { importBatchId: ":importBatchId" },
        starPattern: true,
      }),
      pathName
    );

    const importBatchIdCollectionsPagesMatch = matchPath(
      appRoutes.importBatchCollections({
        params: { importBatchId: ":importBatchId" },
        starPattern: true,
      }),
      pathName
    );

    return (
      importBatchIdNotesPagesMatch?.params.importBatchId ||
      importBatchIdCollectionsPagesMatch?.params.importBatchId
    );
  }
}
